import React from 'react';
import Paper from '@material-ui/core/Paper';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';

import { connect } from 'react-redux';
import { STATUS_COLORS } from 'consts/mapConfig';
import UserSessionDataHandler from 'auth/UserSessionsDataHandler';
import Icons from 'consts/icons';
import ExportButton from 'components/common/components/ExportButton/ExportButtonv2';
import TablePaginationElement from './TablePaginationElement';
import {
  StyledTableCell,
  StyledTableRow,
  tableStyles,
} from './CustomizedTableUseStyles';
import ObjectsRender from '../drawMap/ObjectsRender';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  const ob = orderBy === 'uid' ? 'id' : orderBy;
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, ob)
    : (a, b) => -descendingComparator(a, b, ob);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
};

function EnhancedTableHead(props) {
  const { classes, headers, order, orderBy, onRequestSort } = props;

  const createSortHandler = property => event => {
    if (property !== 'status') {
      onRequestSort(event, property);
    }
  };

  return (
    <TableHead>
      <TableRow>
        {headers.map((header, index) =>
          header ? (
            <StyledTableCell
              key={index}
              align={
                header === 'Uid' || header === 'Id' || header === 'Name'
                  ? 'left'
                  : 'center'
              }
              sortDirection={orderBy === header.toLowerCase() ? order : false}
            >
              {header !== 'Status' ? (
                <>
                  <TableSortLabel
                    active={orderBy === header.toLowerCase()}
                    direction={orderBy === header.toLowerCase() ? order : 'asc'}
                    onClick={createSortHandler(header.toLowerCase())}
                    classes={{
                      root: `${classes.CustomizedTable}__container__table__sort`,
                      active: `${classes.CustomizedTable}__container__table__sort--active`,
                      icon: `${classes.CustomizedTable}__container__table__sort--active`,
                    }}
                  >
                    {header}
                    {orderBy === header.toLowerCase() ? (
                      <span
                        className={`${classes.CustomizedTable}__container__table__sort__visually-hidden`}
                      >
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </>
              ) : (
                header
              )}
            </StyledTableCell>
          ) : null
        )}
        <StyledTableCell style={{ width: 8, padding: 0, minWidth: 8 }} />
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  headers: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
};

const CustomizedTable = props => {
  const {
    headers,
    data,
    onClickFunction,
    total,
    rowsPerPage,
    setRowsPerPage,
    currentPage,
    setCurrentPage,
    selectedRow,
    dashboard,
    getFontSize,
    filtersDataObject,
    filtersUrl,
    filterAdd,
    idItemOnClick,
    smallTable,
  } = props;
  const [rowsPerPageOptions] = React.useState(['10', '25', '50']);
  const [selectedRowIndex, setSelectedRowIndex] = React.useState(null);

  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('start');

  const classes = tableStyles();

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setCurrentPage(0);
  };

  const handleRowClicked = (row, index) => {
    onClickFunction(row, index);
    setSelectedRowIndex(index);
  };

  React.useEffect(() => {
    if (idItemOnClick !== null)
      setSelectedRowIndex(data.findIndex(row => row.id === idItemOnClick));
  }, [idItemOnClick]);

  React.useEffect(() => {
    if (!selectedRow) setSelectedRowIndex(-1);
  }, [selectedRow]);

  const handleIconActive = (value, index) =>
    value ? (
      <Icons.Actions.Done
        className={`${classes.CustomizedTable}__container__table__body__row__cell--active--true`}
      />
    ) : (
      <Icons.Actions.Clear
        className={`${classes.CustomizedTable}__container__table__body__row__cell--active--false`}
      />
    );

  const handleColorStatus = (value, index) => (
    <span
      className={`${classes.CustomizedTable}__container__table__body__row__cell--status`}
      style={{
        color:
          selectedRowIndex === index ? '#ffffff' : STATUS_COLORS[value]?.stroke,
        backgroundColor:
          selectedRowIndex === index ? STATUS_COLORS[value]?.stroke : '',
        borderColor:
          selectedRowIndex === index ? STATUS_COLORS[value]?.stroke : '',
      }}
    >
      {value}
    </span>
  );

  const handleKeyOption = (key, value, index) => {
    if (key === 'active') return handleIconActive(value, index);
    if (key === 'status') return handleColorStatus(value, index);
    return value;
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  // 200 for missions - 100% for zones table
  const maxHeight = headers.includes('Name') ? '100%' : 200;

  return (
    <>
      <TableContainer
        component={Paper}
        className={`${
          dashboard
            ? `${classes.CustomizedTable}__container`
            : `${classes.CustomizedTable}__container__table`
        } ${smallTable ? `${classes.CustomizedTable}--small-table` : ''}`}
        style={{ maxHeight: maxHeight, overflowY: 'auto', overflowX: 'hidden' }}
      >
        <Table
          className={`${classes.CustomizedTable}__container__table__body`}
          aria-labelledby="tableTitle"
          aria-label="sticky enhanced table"
          stickyHeader
          style={{ tableLayout: 'fixed', width: '100%' }}
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            headers={headers}
          />
          <TableBody>
            {data.length ? (
              stableSort(data, getComparator(order, orderBy)).map(
                (row, index) => (
                  <StyledTableRow
                    hover
                    className={
                      selectedRowIndex === index
                        ? `${classes.CustomizedTable}__container__table__body__row--active`
                        : ''
                    }
                    style={{ fontSize: `${getFontSize}rem` }}
                    onClick={
                      onClickFunction
                        ? () => handleRowClicked(row, index)
                        : null
                    }
                    tabIndex={-1}
                    key={index}
                  >
                    {Object.keys(row).map((key, i) =>
                      key !== 'uid' ? (
                        <StyledTableCell
                          key={i}
                          align="left"
                          style={{
                            fontSize: `${UserSessionDataHandler.getFontSize()}rem`,
                            textAlign:
                              key === 'id' || key === 'name'
                                ? 'left'
                                : 'center',
                          }}
                        >
                          {handleKeyOption(key, row[key], index)}
                        </StyledTableCell>
                      ) : null
                    )}
                  </StyledTableRow>
                )
              )
            ) : (
              <StyledTableRow>
                <StyledTableCell
                  style={{
                    fontSize: `${getFontSize}rem`,
                  }}
                >
                  <span className={classes.NoData}> - </span>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={`${classes.CustomizedTable}__container__pagination`}>
        {rowsPerPage ? (
          <TablePaginationElement
            rowsPerPageOptions={rowsPerPageOptions}
            wholeDataLength={total}
            tableRowsPerPage={rowsPerPage}
            tablePaginationNumber={currentPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            drones={false}
          />
        ) : null}
        {filtersUrl ? (
          <ExportButton
            requestData={{
              url: filtersUrl,
              paramsObj: filtersDataObject,
            }}
          />
        ) : null}
        {filterAdd ? (
          <div
            className={`${classes.CustomizedTable}__container__pagination--white-space`}
          ></div>
        ) : null}
      </div>
    </>
  );
};

CustomizedTable.propTypes = {
  headers: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  onClickFunction: PropTypes.func,
  rowsPerPage: PropTypes.number,
  setRowsPerPage: PropTypes.func,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
  total: PropTypes.number,
  selectedRow: PropTypes.object,
  dashboard: PropTypes.bool,
  getFontSize: PropTypes.number,
  filtersDataObject: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  idItemOnClick: PropTypes.string,
  filtersUrl: PropTypes.string,
  filterAdd: PropTypes.bool,
  smallTable: PropTypes.bool,
};

const mapStateToProps = state => ({
  getFontSize: state.getFontSize,
});

export default connect(mapStateToProps)(CustomizedTable);